/**
 * The multi-line sibling of `Input`, which the kit did not have.
 *
 * Fourteen hand-rolled `<textarea>` elements exist across `apps/console/src`,
 * plus three in `packages/ui` itself, and `RfxIntake.tsx:37` says why in a
 * comment: *"a raw `<textarea>` with a module CSS class, because `@solum/ui`
 * exports no…"*. Each of those is a small visual dialect — its own border, its
 * own focus ring, its own idea of padding — in a product whose whole argument
 * is one material.
 *
 * `forwardRef`, exactly like `Input`, because the ref has to reach the real
 * element: react-hook-form's `register()` puts one there, and anything that
 * reads a caret needs it too.
 */
import { forwardRef, type TextareaHTMLAttributes } from 'react'
import { cx } from './cx'
import s from './Textarea.module.css'

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** A code slot: JSON, a DSL, an HTML body. Sets the BRAND's monospace face
   *  (`--font-mono`) and the tighter measure code wants, and nothing else —
   *  the material, the states and the focus ring stay the one value slot.
   *
   *  It is a variant rather than a host stylesheet override because the four
   *  code editors in the tree each invented their own: 12.5px/1.5 in
   *  `rules.module.css`, 12.5px/1.65 in `templateStudio.module.css`, an
   *  inline `fontSize: 12` in `TemplateDesigner.tsx`, and three different
   *  font stacks between them. A `className` could not have fixed that
   *  safely: nothing in this repo declares `@layer`, so whether a host rule
   *  beats `.textarea` depends on bundler emission order. */
  mono?: boolean
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea({ className, mono, ...rest }, ref) {
    return <textarea ref={ref} className={cx(s.textarea, mono && s.mono, className)} {...rest} />
  },
)
